/**
 * BulkLinkDialog - Dialog for bulk linking external tasks to internal tasks
 *
 * Allows users to link multiple external tasks to internal tasks by selecting:
 * - Task Types: Link to all internal tasks of specific types
 * - Task Groups: Link to all internal tasks in specific groups
 */

import * as React from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { enhancedApi as externalTaskApi } from '@/services/api/generated/externalTaskService';
import { tasksApiClient } from '@/services/api/tasksService';

interface BulkLinkDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** IDs of the external tasks to link */
  externalTaskIds: string[];
  /** Callback after successful linking */
  onSuccess?: () => void;
}

export const BulkLinkDialog: React.FC<BulkLinkDialogProps> = ({
  isOpen,
  onClose,
  externalTaskIds,
  onSuccess,
}) => {
  // Fetch task types and groups
  const { data: taskTypes, isLoading: isLoadingTypes } =
    tasksApiClient.useGetTaskTypesQuery();
  const { data: taskGroups, isLoading: isLoadingGroups } =
    tasksApiClient.useGetTaskGroupsQuery();

  // Bulk link mutation
  const [bulkLink, { isLoading: isLinking }] =
    externalTaskApi.useExternalTaskBulkLinkMutation();

  // Selection state
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = React.useState<
    Set<string>
  >(new Set());
  const [selectedTaskGroupIds, setSelectedTaskGroupIds] = React.useState<
    Set<string>
  >(new Set());

  // Reset selections when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedTaskTypeIds(new Set());
      setSelectedTaskGroupIds(new Set());
    }
  }, [isOpen]);

  // Handle task type selection
  const handleToggleTaskType = React.useCallback((taskTypeId: string) => {
    setSelectedTaskTypeIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskTypeId)) {
        newSet.delete(taskTypeId);
      } else {
        newSet.add(taskTypeId);
      }
      return newSet;
    });
  }, []);

  // Handle task group selection
  const handleToggleTaskGroup = React.useCallback((taskGroupId: string) => {
    setSelectedTaskGroupIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskGroupId)) {
        newSet.delete(taskGroupId);
      } else {
        newSet.add(taskGroupId);
      }
      return newSet;
    });
  }, []);

  // Handle submit
  const handleSubmit = React.useCallback(async () => {
    try {
      await bulkLink({
        bulkLinkTasksSchema: {
          external_task_ids: externalTaskIds,
          task_type_ids:
            selectedTaskTypeIds.size > 0
              ? Array.from(selectedTaskTypeIds)
              : undefined,
          task_group_ids:
            selectedTaskGroupIds.size > 0
              ? Array.from(selectedTaskGroupIds)
              : undefined,
        },
      }).unwrap();

      // Success - close dialog and notify parent
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to bulk link tasks:', error);
      // Error handling is managed by RTK Query
    }
  }, [
    bulkLink,
    externalTaskIds,
    selectedTaskTypeIds,
    selectedTaskGroupIds,
    onClose,
    onSuccess,
  ]);

  const isLoading = isLoadingTypes || isLoadingGroups;
  const hasSelection =
    selectedTaskTypeIds.size > 0 || selectedTaskGroupIds.size > 0;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Bulk Link External Tasks</DialogTitle>
      <DialogDescription>
        Link {externalTaskIds.length} external task
        {externalTaskIds.length !== 1 ? 's' : ''} to internal tasks by selecting
        task types or groups. Tasks in the same matter will be linked
        automatically.
      </DialogDescription>

      <DialogBody>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Task Types Section */}
            <Field>
              <Label className="font-semibold text-base">
                Task Types (Optional)
              </Label>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                {taskTypes && taskTypes.length > 0 ? (
                  taskTypes.map((taskType) => (
                    <div
                      key={taskType.id}
                      className="flex items-center gap-2 py-1"
                    >
                      <Checkbox
                        checked={selectedTaskTypeIds.has(taskType.id)}
                        onChange={() => handleToggleTaskType(taskType.id)}
                      />
                      <button
                        type="button"
                        onClick={() => handleToggleTaskType(taskType.id)}
                        className="flex-1 text-left text-sm hover:bg-gray-50 px-2 py-1 rounded"
                      >
                        <div className="font-medium">{taskType.name}</div>
                        {taskType.description && (
                          <div className="text-xs text-gray-500">
                            {taskType.description}
                          </div>
                        )}
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No task types available
                  </p>
                )}
              </div>
              {selectedTaskTypeIds.size > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTaskTypeIds.size} type
                  {selectedTaskTypeIds.size !== 1 ? 's' : ''} selected
                </p>
              )}
            </Field>

            {/* Task Groups Section */}
            <Field>
              <Label className="font-semibold text-base">
                Task Groups (Optional)
              </Label>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-3">
                {taskGroups && taskGroups.length > 0 ? (
                  taskGroups.map((taskGroup) => (
                    <div
                      key={taskGroup.id}
                      className="flex items-center gap-2 py-1"
                    >
                      <Checkbox
                        checked={selectedTaskGroupIds.has(taskGroup.id)}
                        onChange={() => handleToggleTaskGroup(taskGroup.id)}
                      />
                      <button
                        type="button"
                        onClick={() => handleToggleTaskGroup(taskGroup.id)}
                        className="flex-1 text-left text-sm hover:bg-gray-50 px-2 py-1 rounded"
                      >
                        <div className="font-medium">{taskGroup.name}</div>
                        {taskGroup.description && (
                          <div className="text-xs text-gray-500">
                            {taskGroup.description}
                          </div>
                        )}
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No task groups available
                  </p>
                )}
              </div>
              {selectedTaskGroupIds.size > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTaskGroupIds.size} group
                  {selectedTaskGroupIds.size !== 1 ? 's' : ''} selected
                </p>
              )}
            </Field>

            {!hasSelection && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  Please select at least one task type or task group to
                  continue.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button plain onClick={onClose} disabled={isLinking}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasSelection || isLinking || isLoading}
        >
          {isLinking ? 'Linking...' : 'Link Tasks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
